import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Flag, Plus, Trash2, X } from 'lucide-react';
import { del, mensagemDeErro } from '../nucleo/api';
import { dataHora, nomePessoa, textoLivre } from '../nucleo/formato';
import { nomesDoSistema, textoDoSistema, type ParametrosSistema } from '../nucleo/texto-sistema';
import { rotuloCampo, valorCru } from '../nucleo/rotulos';
import { Botao } from './base';
import { Popover } from './menu';
import { Confirmar } from './sobreposicao';
import { useToast } from './toast';
import type { DadosChatter, EventoRelacionado } from './chatter';

type Mensagem = DadosChatter['mensagens'][number];

/** O marco do fim da linha do tempo (a bandeirola). Lista de 30/10 (B), item 21: com `link`, o rótulo termina no link. */
export interface MarcoLinha {
  rotulo: string;
  data: string;
  link?: { para: string; rotulo: string } | null;
}
export const TIPOS_LINHA = ['alteracoes', 'anotacoes', 'tarefas', 'emails'] as const;
export type TipoLinha = (typeof TIPOS_LINHA)[number];

/** Texto de sistema das atividades do Pipeline ("Ligação agendada: ...", "Atividade concluída: ..."). */
const TAREFA = /^(Atividade (concluída|cancelada|editada|adiada|transferida|reatribuída|excluída)|[^:]{1,40} agendada): /;

/** Ajustes do CRM (27/10, F3 e C9): o tipo de cada evento para o filtro "Exibir" da linha do tempo. */
export function tipoDoEvento(m: Pick<Mensagem, 'tipo' | 'corpo' | 'parametros'>): TipoLinha {
  if (m.tipo === 'nota' || m.tipo === 'mensagem') return 'anotacoes';
  if (m.tipo === 'sistema' && (m.parametros as { modelo?: string } | null | undefined)?.modelo?.endsWith('.email')) return 'emails';
  if (m.tipo === 'sistema' && TAREFA.test(m.corpo)) return 'tarefas';
  return 'alteracoes';
}

/**
 * Ajustes do CRM (27/10, F3 e C9): a variante "linha" do Chatter, o histórico do RD. Faixa com "Exibir" (tipos marcáveis,
 * aplicados no botão) e "Criar anotação" (abre o campo de sempre: mensagem ou nota, menção e anexo); a linha do tempo com o
 * trilho e uma bolinha por evento (decorativos), autor em negrito, o texto e a data; a própria anotação tem a lixeira. No fim,
 * o marco da criação com a bandeirola. O modo discreto é o mesmo dos cartões (`textoLivre`, valores pela censura).
 */
export function LinhaDoTempo({
  d,
  eu,
  entidade,
  registroId,
  nomes,
  rotulosCampos,
  valorHistorico,
  marco,
  linkDoSistema,
  cabecalho,
  compositor,
  anexos,
  relacionados,
  herdados,
}: {
  d: DadosChatter;
  eu: string;
  entidade: string;
  registroId: string;
  nomes: (string | null | undefined)[];
  rotulosCampos?: Record<string, string>;
  valorHistorico: (campo: string, v: unknown, entidade?: string) => string;
  marco?: MarcoLinha;
  linkDoSistema?: (p: ParametrosSistema) => string | null;
  cabecalho: ReactNode;
  compositor: ReactNode;
  anexos?: ReactNode;
  /** Lista de 30/10 (B), item 3.1: eventos de registros ligados, na mesma linha do tempo (só leitura). */
  relacionados?: EventoRelacionado[];
  /**
   * Review rlb-1 (SPEC-M2): eventos herdados (o histórico da negociação original até a cópia), abaixo do marco, só leitura e
   * com o mesmo filtro "Exibir".
   */
  herdados?: EventoRelacionado[];
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [tipos, setTipos] = useState<TipoLinha[]>([]);
  const [marcados, setMarcados] = useState<TipoLinha[]>([]);
  const [escrevendo, setEscrevendo] = useState(false);
  const [excluir, setExcluir] = useState<string | null>(null);
  const apagar = useMutation({
    mutationFn: (id: string) => del(`/chatter/${entidade}/${registroId}/${id}`),
    onSuccess: async () => {
      setExcluir(null);
      toast.sucesso(t('chatter.linha.excluida'));
      await qc.invalidateQueries({ queryKey: ['chatter', entidade, registroId] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const instante = (m: { created_at: string }) => new Date(m.created_at).getTime();
  const todas: (Mensagem | EventoRelacionado)[] = relacionados?.length ? [...d.mensagens, ...relacionados].sort((a, b) => instante(b) - instante(a)) : d.mensagens;
  const filtrar = (m: Mensagem | EventoRelacionado) => !tipos.length || tipos.includes(tipoDoEvento(m));
  const visiveis = todas.filter(filtrar);
  const herdadosVisiveis = (herdados ?? []).filter(filtrar).sort((a, b) => instante(b) - instante(a));
  const rotuloFiltro = tipos.length ? t('chatter.linha.nTipos', { count: tipos.length }) : t('chatter.linha.tipos.todos');
  const alternar = (x: TipoLinha) => setMarcados((v) => (v.includes(x) ? v.filter((y) => y !== x) : [...v, x]));

  const evento = (m: Mensagem | EventoRelacionado, herdado = false) => {
    const autor = <strong className="font-semibold text-tinta-1">{m.autor_nome ? nomePessoa(m.autor_nome) : t('chatter.linha.sistemaAutor')}</strong>;
    const anotacao = m.tipo === 'nota' || m.tipo === 'mensagem';
    const rel = 'origem' in m ? m : null;
    return (
      <li key={`${herdado ? 'h-' : ''}${rel ? `${rel.entidade}-${m.id}` : m.id}`} className="linha-tempo-evento group text-sm text-tinta-1" data-evento-historico={tipoDoEvento(m)} data-evento-relacionado={rel?.entidade} data-evento-herdado={herdado ? '' : undefined}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 break-words">
            {rel?.origem && <p className="mb-0.5 text-xs font-medium text-tinta-3" data-origem-evento="">{rel.origem}</p>}
            {rel?.conteudo ? (
              <p className="whitespace-pre-wrap">
                {autor} {rel.conteudo}
              </p>
            ) : anotacao ? (
              <>
                <p>
                  {autor} {m.tipo === 'nota' ? t('chatter.linha.fezAnotacao') : t('chatter.linha.enviouMensagem')}
                </p>
                <p className={`mt-1 whitespace-pre-wrap ${m.tipo === 'nota' ? 'border-l-2 border-dourado pl-2' : ''}`}>{textoLivre(m.corpo, ...nomes)}</p>
              </>
            ) : m.tipo === 'historico' && m.alteracoes ? (
              <ul className="flex flex-col gap-0.5">
                {m.alteracoes.map((a) => (
                  <li key={a.campo}>
                    {autor} {t('chatter.historico')} <strong>{rotulosCampos?.[a.campo] ?? rotuloCampo(a.campo)}</strong> {t('chatter.de')} <span className={`text-tinta-3${valorCru(a.campo, a.de) ? ' numero' : ''}`}>{valorHistorico(a.campo, a.de, rel?.entidade)}</span> {t('chatter.para')}{' '}
                    <span className={valorCru(a.campo, a.para) ? 'numero' : undefined}>{valorHistorico(a.campo, a.para, rel?.entidade)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="whitespace-pre-wrap">
                {autor} {textoLivre(textoDoSistema(m.corpo, m.parametros), ...nomes, ...nomesDoSistema(m.parametros))}
                {(() => {
                  const para = m.tipo === 'sistema' && m.parametros && linkDoSistema ? linkDoSistema(m.parametros) : null;
                  return para ? (
                    <>
                      {' '}
                      <Link to={para} className="font-medium text-acento-forte hover:underline" data-link-evento="">
                        {t('chatter.linha.abrirRegistro')}
                      </Link>
                    </>
                  ) : null;
                })()}
              </p>
            )}
            <p className="numero mt-1 text-xs text-tinta-3">{dataHora(m.created_at)}</p>
          </div>
          {anotacao && !rel && m.autor_id === eu && (
            <Botao
              tamanho="sm"
              variante="fantasma"
              className="shrink-0 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
              aria-label={t('chatter.linha.excluirAnotacao')}
              title={t('chatter.linha.excluirAnotacao')}
              icone={<Trash2 className="size-4" />}
              onClick={() => setExcluir(m.id)}
              data-excluir-anotacao=""
            />
          )}
        </div>
      </li>
    );
  };

  return (
    <section aria-label={t('chatter.titulo')} className="flex min-w-0 flex-col gap-4" data-chatter-linha="">
      {cabecalho}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">{t('chatter.linha.exibir')}</span>
        <Popover rotulo={t('chatter.linha.exibir')} texto={<span className="flex items-center gap-1">{rotuloFiltro}<ChevronDown className="size-4" aria-hidden /></span>} icone={null} classePainel="left-0 w-60" data-filtro-historico="">
          <fieldset className="flex flex-col gap-1">
            <legend className="kicker mb-1">{t('chatter.linha.exibir')}</legend>
            <label className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-sup-3">
              <input type="checkbox" checked={!marcados.length} onChange={() => setMarcados([])} />
              {t('chatter.linha.tipos.todos')}
            </label>
            {TIPOS_LINHA.map((x) => (
              <label key={x} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-sup-3">
                <input type="checkbox" checked={marcados.includes(x)} onChange={() => alternar(x)} data-tipo-historico={x} />
                {t(`chatter.linha.tipos.${x}`)}
              </label>
            ))}
          </fieldset>
          <div className="mt-2 flex justify-end border-t border-borda pt-2">
            <Botao tamanho="sm" variante="primario" onClick={() => setTipos(marcados)} data-aplicar-filtro="">
              {t('chatter.linha.aplicar')}
            </Botao>
          </div>
        </Popover>
        <span className="flex-1" />
        <Botao variante="secundario" icone={escrevendo ? <X className="size-4" /> : <Plus className="size-4" />} aria-expanded={escrevendo} onClick={() => setEscrevendo((x) => !x)} data-criar-anotacao="">
          {escrevendo ? t('chatter.linha.fecharAnotacao') : t('chatter.linha.criarAnotacao')}
        </Botao>
      </div>
      {escrevendo && compositor}
      {anexos}

      <ol className="linha-tempo flex flex-col gap-6 py-2">
        {visiveis.length === 0 && <li className="linha-tempo-evento linha-tempo-marco text-sm text-tinta-3">{t('chatter.linha.vazio')}</li>}
        {visiveis.map((m) => evento(m))}
        {marco && (
          <li className="linha-tempo-evento linha-tempo-marco text-sm" data-marco-historico="">
            <span aria-hidden className="absolute top-0 left-0 grid size-8 place-items-center rounded-full bg-acento-forte text-sup-1">
              <Flag className="size-4" />
            </span>
            <p className="pt-1 font-semibold">
              {marco.rotulo}
              {marco.link && (
                <>
                  {' '}
                  <Link to={marco.link.para} className="text-acento-forte hover:underline" data-marco-link="">
                    {marco.link.rotulo}
                  </Link>
                </>
              )}
            </p>
            <p className="numero mt-1 text-xs text-tinta-3">{dataHora(marco.data)}</p>
          </li>
        )}
        {herdadosVisiveis.map((m) => evento(m, true))}
      </ol>
      <Confirmar aberto={!!excluir} aoFechar={() => setExcluir(null)} perigoso titulo={t('chatter.linha.excluirTitulo')} texto={t('chatter.linha.excluirTexto')} carregando={apagar.isPending} aoConfirmar={() => excluir && apagar.mutate(excluir)} />
    </section>
  );
}
